import { Fragment, useMemo, useRef } from 'react';
import { useDrag } from '@use-gesture/react';
import {
  buildMaskPathCommands,
  isValidQuad,
  surfaceDestToUv,
  surfaceUvToDest,
  type CornerIndex,
  type Point,
  type SurfaceMask,
} from '@cornerstone/shared';
import {
  EDGE_CORNERS,
  useCalibrationV2Store,
  useActiveSurfaceV2,
  type EdgeId,
} from '../store/calibrationV2';
import type { MediaTextureSourceV2 } from '../renderer/warpV2Renderer';
import { useWarpV2Renderer } from '../renderer/useWarpV2Renderer';
import { cn } from '../utils/cn';
import { Handle, type CornerLabel } from './Handle';

// =============================================================================
// Calibration v2 캔버스 — PLAN §5.1/§5.2.
// 출력 프리뷰(WebGL: 패턴/미디어 + warp + mask) 위에 편집 모드별 오버레이:
//   [코너핀] 4 corner + 4 edge 핸들
//   [메시]   그리드 점 핸들 (다중 선택 + Shift 축 제한)
//   [마스크] 도형 정점/베지어 핸들, 엣지 더블클릭 = 정점 추가
// =============================================================================

const CORNER_LABELS: readonly CornerLabel[] = ['TL', 'TR', 'BR', 'BL'];
const EDGES: readonly EdgeId[] = ['top', 'right', 'bottom', 'left'];

export function SurfaceCanvasV2({ media }: { media: MediaTextureSourceV2 | null }) {
  const previewRef = useRef<HTMLDivElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);

  const surface = useActiveSurfaceV2();
  const surfaces = useCalibrationV2Store((s) => s.surfaces);
  const activeSurfaceId = useCalibrationV2Store((s) => s.activeSurfaceId);
  const editorMode = useCalibrationV2Store((s) => s.editorMode);
  const selection = useCalibrationV2Store((s) => s.selection);
  const selectCorner = useCalibrationV2Store((s) => s.selectCorner);
  const moveCorner = useCalibrationV2Store((s) => s.moveCorner);
  const translateEdge = useCalibrationV2Store((s) => s.translateEdge);
  const toggleMeshPoint = useCalibrationV2Store((s) => s.toggleMeshPoint);
  const selectMeshPoints = useCalibrationV2Store((s) => s.selectMeshPoints);
  const moveMeshPoint = useCalibrationV2Store((s) => s.moveMeshPoint);
  const translateMeshPoints = useCalibrationV2Store((s) => s.translateMeshPoints);
  const toggleMaskVertex = useCalibrationV2Store((s) => s.toggleMaskVertex);
  const selectMaskVertices = useCalibrationV2Store((s) => s.selectMaskVertices);
  const moveMaskVertex = useCalibrationV2Store((s) => s.moveMaskVertex);
  const translateMaskVertices = useCalibrationV2Store((s) => s.translateMaskVertices);
  const moveMaskHandle = useCalibrationV2Store((s) => s.moveMaskHandle);
  const toggleMaskVertexCurve = useCalibrationV2Store((s) => s.toggleMaskVertexCurve);
  const addMaskVertex = useCalibrationV2Store((s) => s.addMaskVertex);
  const beginDrag = useCalibrationV2Store((s) => s.beginDrag);
  const endDrag = useCalibrationV2Store((s) => s.endDrag);
  const bypassWarp = useCalibrationV2Store((s) => s.bypassWarp);

  useWarpV2Renderer(canvasRef, media);

  const corners = surface?.corners ?? null;
  const mesh = surface?.mesh ?? null;
  const mask = surface?.mask ?? null;
  const displayMask = useMemo<SurfaceMask | null>(() => {
    if (!surface?.mask) return null;
    const mapPoint = (p: Point) => surfaceUvToDest(surface, p);
    return {
      ...surface.mask,
      vertices: surface.mask.vertices.map((v) => ({
        p: mapPoint(v.p),
        ...(v.h1 ? { h1: mapPoint(v.h1) } : {}),
        ...(v.h2 ? { h2: mapPoint(v.h2) } : {}),
      })),
    };
  }, [surface]);
  const valid = corners ? isValidQuad(corners) : true;

  /** clientX/Y → 정규화 [0,1] destination 좌표. */
  const toLocal = (clientX: number, clientY: number): Point | null => {
    const rect = previewRef.current?.getBoundingClientRect();
    if (!rect) return null;
    return {
      x: (clientX - rect.left) / rect.width,
      y: (clientY - rect.top) / rect.height,
    };
  };

  /** clientX/Y → Surface 로컬 마스크 UV. */
  const toMaskLocal = (clientX: number, clientY: number): Point | null => {
    const dest = toLocal(clientX, clientY);
    return dest && surface ? surfaceDestToUv(surface, dest) : null;
  };

  /** Shift 축 제한 — 시작점 기준 지배 축만 이동. */
  const axisLock = (start: Point, g: Point, shift: boolean): Point => {
    if (!shift) return g;
    return Math.abs(g.x - start.x) >= Math.abs(g.y - start.y)
      ? { x: g.x, y: start.y }
      : { x: start.x, y: g.y };
  };

  // -------- 코너핀: corner 드래그 --------
  const cornerStartRef = useRef<Point | null>(null);
  const bindCorner = useDrag(
    ({ args, first, last, xy, tap, shiftKey }) => {
      const index = args[0] as CornerIndex;
      if (tap) {
        selectCorner(index);
        return;
      }
      const g = toLocal(xy[0], xy[1]);
      if (!g || !corners) return;
      if (first) {
        beginDrag();
        selectCorner(index);
        cornerStartRef.current = { ...corners[index] };
      }
      const start = cornerStartRef.current ?? g;
      moveCorner(index, axisLock(start, g, shiftKey));
      if (last) {
        endDrag();
        cornerStartRef.current = null;
      }
    },
    { filterTaps: true, threshold: 3 },
  );

  // -------- 코너핀: edge 드래그 (변 2점 평행 이동) --------
  const bindEdge = useDrag(
    ({ args, first, last, delta, tap }) => {
      if (tap) return;
      const edge = args[0] as EdgeId;
      const rect = previewRef.current?.getBoundingClientRect();
      if (!rect) return;
      if (first) beginDrag();
      translateEdge(edge, delta[0] / rect.width, delta[1] / rect.height);
      if (last) endDrag();
    },
    { filterTaps: true, threshold: 3 },
  );

  // -------- 메시: 그리드 점 드래그 --------
  const meshStartRef = useRef<Point | null>(null);
  const bindMeshPoint = useDrag(
    ({ args, first, last, xy, delta, tap, shiftKey }) => {
      const index = args[0] as number;
      if (tap) {
        if (shiftKey) toggleMeshPoint(index);
        else selectMeshPoints([index]);
        return;
      }
      if (!mesh) return;
      const rect = previewRef.current?.getBoundingClientRect();
      const g = toLocal(xy[0], xy[1]);
      if (!rect || !g) return;
      const selected =
        selection?.kind === 'mesh' && selection.indices.includes(index)
          ? selection.indices
          : null;
      if (first) {
        beginDrag();
        if (!selected) selectMeshPoints([index]);
        meshStartRef.current = mesh.points[index] ? { ...mesh.points[index]! } : g;
      }
      if (selected && selected.length > 1) {
        // 그룹 이동 — 프레임 delta로 전체 평행 이동.
        translateMeshPoints(selected, delta[0] / rect.width, delta[1] / rect.height);
      } else {
        const start = meshStartRef.current ?? g;
        moveMeshPoint(index, axisLock(start, g, shiftKey));
      }
      if (last) {
        endDrag();
        meshStartRef.current = null;
      }
    },
    { filterTaps: true, threshold: 3 },
  );

  // -------- 마스크: 정점 드래그 --------
  const maskStartRef = useRef<Point | null>(null);
  const maskPointerRef = useRef<Point | null>(null);
  const bindMaskVertex = useDrag(
    ({ args, first, last, xy, tap, shiftKey }) => {
      const index = args[0] as number;
      if (tap) {
        if (shiftKey) toggleMaskVertex(index);
        else selectMaskVertices([index]);
        return;
      }
      if (!mask) return;
      const g = toMaskLocal(xy[0], xy[1]);
      if (!g) return;
      const selected =
        selection?.kind === 'maskVertex' && selection.indices.includes(index)
          ? selection.indices
          : null;
      if (first) {
        beginDrag();
        if (!selected) selectMaskVertices([index]);
        maskStartRef.current = mask.vertices[index]
          ? { ...mask.vertices[index]!.p }
          : g;
        maskPointerRef.current = g;
      }
      if (selected && selected.length > 1) {
        const previous = maskPointerRef.current ?? g;
        translateMaskVertices(selected, g.x - previous.x, g.y - previous.y);
        maskPointerRef.current = g;
      } else {
        const start = maskStartRef.current ?? g;
        moveMaskVertex(index, axisLock(start, g, shiftKey));
      }
      if (last) {
        endDrag();
        maskStartRef.current = null;
        maskPointerRef.current = null;
      }
    },
    { filterTaps: true, threshold: 3 },
  );

  // -------- 마스크: 베지어 핸들 드래그 --------
  const bindMaskHandle = useDrag(
    ({ args, first, last, xy, tap }) => {
      if (tap) return;
      const index = args[0] as number;
      const which = args[1] as 'h1' | 'h2';
      const g = toMaskLocal(xy[0], xy[1]);
      if (!g) return;
      if (first) beginDrag();
      moveMaskHandle(index, which, g);
      if (last) endDrag();
    },
    { filterTaps: true, threshold: 3 },
  );

  const meshCols = mesh?.cols ?? 0;
  const meshStride = meshCols + 1;
  const activeSurfaceIndex = Math.max(0, surfaces.findIndex((item) => item.id === activeSurfaceId));
  const activeColor = surface?.color ?? '#38BDF8';

  return (
    <div
      className={cn(
        'bg-panel border rounded-md overflow-hidden relative min-h-0 flex-1 flex flex-col',
        valid ? 'border-line' : 'border-red',
      )}
    >
      <header className="flex justify-between items-center px-4 py-3 border-b border-line bg-panel-2 flex-none">
        <div className="flex items-center gap-2.5">
          <span className="font-mono text-[12px] font-semibold uppercase tracking-monoWide text-text-dimmer px-1.5 py-[2px] bg-panel-3 rounded-sm">
            SURFACE·{String(activeSurfaceIndex + 1).padStart(2, '0')}
          </span>
          <span className="font-display text-[19px] font-semibold tracking-[-0.01em]">
            Output Preview
          </span>
          <span className="font-mono text-[12px] font-semibold uppercase tracking-monoWide px-1.5 py-[2px] rounded-sm text-blue bg-blue/10 border border-blue/30">
            {editorMode === 'corner' ? '서페이스' : editorMode === 'mesh' ? '메시' : '마스크'}
          </span>
        </div>
        <div className="font-mono text-[13px] text-text-dim flex items-center gap-3">
          {mesh && (
            <span>
              mesh {mesh.cols}×{mesh.rows}
            </span>
          )}
          {mask && (
            <span>
              mask {mask.kind} · {mask.vertices.length}pt
              {mask.invert ? ' · invert' : ''}
            </span>
          )}
          {bypassWarp && <span className="text-purple">bypass</span>}
          {!valid && <span className="text-red">⚠ invalid</span>}
        </div>
      </header>

      <div
        ref={previewRef}
        className={cn(
          'relative overflow-hidden aspect-[16/9]',
          'bg-[linear-gradient(135deg,#0a0b0d_0%,#0d0e12_100%)]',
        )}
      >
        <canvas
          ref={canvasRef}
          className="absolute inset-0 w-full h-full block pointer-events-none"
        />

        {/* ================= 코너핀 모드 ================= */}
        {editorMode === 'corner' && corners && (
          <>
            {/* Edge grab lines — 변 전체 평행 이동 */}
            <svg
              className="absolute inset-0 w-full h-full"
              viewBox="0 0 100 100"
              preserveAspectRatio="none"
              style={{ pointerEvents: 'none' }}
            >
              {EDGES.map((edge) => {
                const [ia, ib] = EDGE_CORNERS[edge];
                const a = corners[ia];
                const b = corners[ib];
                return (
                  <g key={edge}>
                    <line
                      x1={a.x * 100}
                      y1={a.y * 100}
                      x2={b.x * 100}
                      y2={b.y * 100}
                      stroke="#F24822"
                      strokeWidth={1}
                      vectorEffect="non-scaling-stroke"
                      opacity={0.22}
                      style={{ pointerEvents: 'none' }}
                    />
                    <line
                      x1={a.x * 100}
                      y1={a.y * 100}
                      x2={b.x * 100}
                      y2={b.y * 100}
                      stroke="transparent"
                      strokeWidth={14}
                      vectorEffect="non-scaling-stroke"
                      strokeLinecap="butt"
                      style={{
                        cursor: 'move',
                        pointerEvents: 'stroke',
                        touchAction: 'none',
                      }}
                      {...(bindEdge(edge) as unknown as Record<string, unknown>)}
                    />
                  </g>
                );
              })}
            </svg>

            {corners.map((pt, i) => {
              const index = i as CornerIndex;
              const selected =
                selection?.kind === 'corner' && selection.index === index;
              return (
                <Handle
                  key={index}
                  label={CORNER_LABELS[index]!}
                  position={pt}
                  selected={selected}
                  showCoord={selected}
                  onSelect={() => selectCorner(index)}
                  bindProps={
                    bindCorner(index) as unknown as Record<string, unknown>
                  }
                />
              );
            })}
          </>
        )}

        {/* ================= 메시 모드 ================= */}
        {editorMode === 'mesh' &&
          (mesh ? (
            <>
              {/* 그리드 라인 */}
              <svg
                className="absolute inset-0 w-full h-full pointer-events-none"
                viewBox="0 0 100 100"
                preserveAspectRatio="none"
              >
                {Array.from({ length: mesh.rows + 1 }, (_, r) => (
                  <polyline
                    key={`r${r}`}
                    points={Array.from({ length: meshStride }, (_, c) => {
                      const p = mesh.points[r * meshStride + c]!;
                      return `${p.x * 100},${p.y * 100}`;
                    }).join(' ')}
                    fill="none"
                    stroke={activeColor}
                    strokeWidth={1}
                    vectorEffect="non-scaling-stroke"
                    opacity={0.45}
                  />
                ))}
                {Array.from({ length: meshStride }, (_, c) => (
                  <polyline
                    key={`c${c}`}
                    points={Array.from({ length: mesh.rows + 1 }, (_, r) => {
                      const p = mesh.points[r * meshStride + c]!;
                      return `${p.x * 100},${p.y * 100}`;
                    }).join(' ')}
                    fill="none"
                    stroke={activeColor}
                    strokeWidth={1}
                    vectorEffect="non-scaling-stroke"
                    opacity={0.45}
                  />
                ))}
              </svg>

              {mesh.points.map((p, i) => {
                const selected =
                  selection?.kind === 'mesh' && selection.indices.includes(i);
                return (
                  <button
                    key={i}
                    type="button"
                    className={cn(
                      'absolute z-20 w-4 h-4 -ml-2 -mt-2 rounded-full border-2 border-white',
                      'shadow-[0_0_0_2px_rgba(0,0,0,0.5)] cursor-grab active:cursor-grabbing',
                      selected ? 'ring-2 ring-accent' : '',
                    )}
                    style={{
                      left: `${p.x * 100}%`,
                      top: `${p.y * 100}%`,
                      backgroundColor: selected ? '#F24822' : activeColor,
                      touchAction: 'none',
                    }}
                    title="메시 점 · 드래그 이동 · Shift+클릭 다중 선택 · Shift+드래그 축 제한"
                    {...(bindMeshPoint(i) as unknown as Record<string, unknown>)}
                  />
                );
              })}
            </>
          ) : (
            <CanvasHint>왼쪽 패널에서 그리드 밀도를 선택하면 메시 편집이 시작됩니다.</CanvasHint>
          ))}

        {/* ================= 마스크 모드 ================= */}
        {editorMode === 'mask' &&
          (mask && displayMask ? (
            <>
              {/* 도형 외곽선 + 엣지 더블클릭(정점 추가) 히트 존 */}
              <svg
                className="absolute inset-0 w-full h-full"
                viewBox="0 0 100 100"
                preserveAspectRatio="none"
                style={{ pointerEvents: 'none' }}
              >
                <path
                  d={maskPathD(displayMask)}
                  fill="none"
                  stroke="#F2A93E"
                  strokeWidth={1.5}
                  vectorEffect="non-scaling-stroke"
                  opacity={0.85}
                />
                {displayMask.vertices.map((v, i) => {
                  const next = displayMask.vertices[(i + 1) % displayMask.vertices.length]!;
                  return (
                    <line
                      key={`edge-${i}`}
                      x1={v.p.x * 100}
                      y1={v.p.y * 100}
                      x2={next.p.x * 100}
                      y2={next.p.y * 100}
                      stroke="transparent"
                      strokeWidth={12}
                      vectorEffect="non-scaling-stroke"
                      style={{ pointerEvents: 'stroke', cursor: 'copy' }}
                      onDoubleClick={(e) => {
                        e.stopPropagation();
                        const g = toMaskLocal(e.clientX, e.clientY);
                        if (g) addMaskVertex(i, g);
                      }}
                    />
                  );
                })}
              </svg>

              {/* 베지어 핸들 (선택된 곡선 정점) */}
              <svg
                className="absolute inset-0 w-full h-full pointer-events-none"
                viewBox="0 0 100 100"
                preserveAspectRatio="none"
              >
                {displayMask.vertices.map((v, i) => {
                  const selected =
                    selection?.kind === 'maskVertex' &&
                    selection.indices.includes(i);
                  if (!selected) return null;
                  return (
                    <Fragment key={`hl-${i}`}>
                      {v.h1 && (
                        <line
                          x1={v.p.x * 100}
                          y1={v.p.y * 100}
                          x2={v.h1.x * 100}
                          y2={v.h1.y * 100}
                          stroke="#C084FC"
                          strokeWidth={1}
                          vectorEffect="non-scaling-stroke"
                          opacity={0.8}
                        />
                      )}
                      {v.h2 && (
                        <line
                          x1={v.p.x * 100}
                          y1={v.p.y * 100}
                          x2={v.h2.x * 100}
                          y2={v.h2.y * 100}
                          stroke="#C084FC"
                          strokeWidth={1}
                          vectorEffect="non-scaling-stroke"
                          opacity={0.8}
                        />
                      )}
                    </Fragment>
                  );
                })}
              </svg>

              {displayMask.vertices.map((v, i) => {
                const selected =
                  selection?.kind === 'maskVertex' && selection.indices.includes(i);
                const curved = Boolean(v.h1 || v.h2);
                return (
                  <Fragment key={`v-${i}`}>
                    <button
                      type="button"
                      className={cn(
                        'absolute z-20 w-4 h-4 -ml-2 -mt-2 border-2 border-white',
                        'shadow-[0_0_0_2px_rgba(0,0,0,0.5)] cursor-grab active:cursor-grabbing',
                        curved ? 'rounded-full' : 'rounded-sm',
                        selected ? 'bg-accent ring-2 ring-accent' : 'bg-[#F2A93E]',
                      )}
                      style={{
                        left: `${v.p.x * 100}%`,
                        top: `${v.p.y * 100}%`,
                        touchAction: 'none',
                      }}
                      title="마스크 정점 · 드래그 이동 · Shift+클릭 다중 선택 · 우클릭 직선↔곡선 · Delete 삭제"
                      onContextMenu={(e) => {
                        e.preventDefault();
                        toggleMaskVertexCurve(i);
                      }}
                      {...(bindMaskVertex(i) as unknown as Record<string, unknown>)}
                    />
                    {selected &&
                      (['h1', 'h2'] as const).map((which) => {
                        const h = v[which];
                        if (!h) return null;
                        return (
                          <button
                            key={which}
                            type="button"
                            className={cn(
                              'absolute z-20 w-3 h-3 -ml-1.5 -mt-1.5 rounded-full',
                              'bg-purple border border-white shadow-[0_0_0_1px_rgba(0,0,0,0.5)]',
                              'cursor-grab active:cursor-grabbing',
                            )}
                            style={{
                              left: `${h.x * 100}%`,
                              top: `${h.y * 100}%`,
                              touchAction: 'none',
                            }}
                            title={`베지어 핸들 (${which})`}
                            {...(bindMaskHandle(i, which) as unknown as Record<
                              string,
                              unknown
                            >)}
                          />
                        );
                      })}
                  </Fragment>
                );
              })}
            </>
          ) : (
            <CanvasHint>
              왼쪽 패널에서 도형 프리셋(▭ ◇ ★ ● ✎)을 선택하면 마스크 편집이 시작됩니다.
            </CanvasHint>
          ))}

        <CornerBrackets />
      </div>
    </div>
  );
}

/** 마스크 경로 → SVG path d (viewBox 0..100). */
function maskPathD(mask: SurfaceMask): string {
  const cmds = buildMaskPathCommands(mask);
  const parts: string[] = [];
  for (const c of cmds) {
    switch (c.op) {
      case 'moveTo':
        parts.push(`M ${c.x * 100} ${c.y * 100}`);
        break;
      case 'lineTo':
        parts.push(`L ${c.x * 100} ${c.y * 100}`);
        break;
      case 'bezierCurveTo':
        parts.push(
          `C ${c.c1x * 100} ${c.c1y * 100}, ${c.c2x * 100} ${c.c2y * 100}, ${c.x * 100} ${c.y * 100}`,
        );
        break;
      case 'closePath':
        parts.push('Z');
        break;
    }
  }
  return parts.join(' ');
}

function CanvasHint({ children }: { children: React.ReactNode }) {
  return (
    <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
      <span className="font-mono text-[14px] font-medium leading-6 text-white bg-black/80 border border-white/80 shadow-lg rounded px-4 py-2.5">
        {children}
      </span>
    </div>
  );
}

function CornerBrackets() {
  const cls = 'absolute w-3 h-3 border-text-dimmer opacity-40 pointer-events-none';
  return (
    <>
      <span className={cn(cls, 'top-2.5 left-2.5 border-l border-t')} />
      <span className={cn(cls, 'top-2.5 right-2.5 border-r border-t')} />
      <span className={cn(cls, 'bottom-2.5 left-2.5 border-l border-b')} />
      <span className={cn(cls, 'bottom-2.5 right-2.5 border-r border-b')} />
    </>
  );
}
